'use client';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React from 'react';
import { Paragraph } from '../ui/paragraph';

export type ProductPriceProps = {
  product: Product;
  className?: {
    main?: string;
    discount?: string;
    discountWrapper?: string;
    price?: string;
    priceAfterDiscount?: string;
    priceBeforeDiscount?: string;
  };
};

export function ProductPrice({ product, className }: ProductPriceProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const currency = generalLayout.currency;
  const currencySettings = generalLayout.currency_settings;
  const { t } = useTranslations();

  // const customer_discount = useGlobalStore(state => state.config.customer?.discount ?? 0);

  const me = useCustomerStore(state => state.me);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler: me?.customer.is_wholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  });

  const unit = templateSettings.product_detail?.show_unit?.enabled ? ` / ${product.unit}` : '';

  if (priceDisplay.priceOnRequest) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex flex-col items-start justify-start gap-1',
        'ea-product-price',
        className?.main,
      )}
    >
      {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
        <Paragraph size="sm" color="muted-foreground" className="whitespace-nowrap">
          {`${t('text1926')} ${formatPrice(
            priceDisplay.mocPrice,
            currency,
            currencySettings,
          )}${unit}`}
        </Paragraph>
      ) : null}
      <div
        className={cn(
          'flex items-baseline justify-start gap-3',
          'ea-product-price__price',
          className?.price,
        )}
      >
        <Paragraph
          size="base"
          className={cn(
            'whitespace-nowrap font-bold',
            'ea-product-price__price-after-discount',
            className?.priceAfterDiscount,
          )}
        >
          {formatPrice(priceDisplay.current, currency, currencySettings)} {unit}
        </Paragraph>
        {priceDisplay.showPreviousPrice ? (
          <del
            className={cn(
              'whitespace-nowrap text-[10px] font-normal text-[#666666] sm:text-xs',
              'ea-product-price__price-before-discount',
              className?.priceBeforeDiscount,
            )}
          >{`${formatPrice(priceDisplay.beforeDiscount, currency, currencySettings)}${unit}`}</del>
        ) : null}
      </div>
      {templateSettings.product_teaser?.price_without_vat?.enabled ? (
        <Paragraph size="sm" color="muted-foreground" className="whitespace-nowrap">
          {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
          {formatPrice(priceDisplay.secondary, currency, currencySettings)} {unit}{' '}
          {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
        </Paragraph>
      ) : null}
    </div>
  );
}
